import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { IFirestoreAlert } from '../../../../Types/firestoreTypes';
import { FirestoreService } from '../../../../Services/FirestoreService';
import { getUserFromLS } from '../../../../utils/getUserFromLS';
import { useFirebaseAuthReady } from '../../../../Services/FirebaseAuthService';

const MAX_RETRIES = 3;
const BASE_RETRY_MS = 2_000;

type AlertWithId = { id: string; data: IFirestoreAlert };

interface AlertContextType {
  alerts: AlertWithId[];
  unreadAlerts: AlertWithId[];
  isLoading: boolean;
  error: string | null;
  deleteAlert: (alertId: string) => Promise<void>;
  deleteMultipleAlerts: (alertIds: string[]) => Promise<void>;
  acknowledgeAlert: (alertId: string) => Promise<void>;
  markAlertAsNew: (alertId: string) => Promise<void>;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertWithId[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useSelector((state: any) => state.auth);
  const user = getUserFromLS();
  const isFirebaseReady = useFirebaseAuthReady(user?.id);

  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  const subscribe = useCallback((userId: string) => {
    unsubRef.current?.();

    const handleUpdate = (newAlerts: AlertWithId[]) => {
      retryCountRef.current = 0;
      setAlerts(newAlerts);
      setError(null);
    };

    const handleError = (err: Error) => {
      console.error('Error in alert listener:', err);
      const isPermissionDenied = (err as any).code === 'permission-denied';

      if (isPermissionDenied && retryCountRef.current < MAX_RETRIES) {
        const delay = BASE_RETRY_MS * 2 ** retryCountRef.current;
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => subscribe(userId), delay);
      } else {
        setError(err.message);
      }
    };

    unsubRef.current = FirestoreService.startListeningToAllAlerts(
      userId,
      handleUpdate,
      handleError
    );
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !isFirebaseReady) {
      setAlerts([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    retryCountRef.current = 0;
    subscribe(user.id);
    setIsLoading(false);

    return () => {
      unsubRef.current?.();
      unsubRef.current = null;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryCountRef.current = 0;
    };
  }, [isAuthenticated, user?.id, isFirebaseReady, subscribe]);

  // Delete single alert
  const deleteAlert = useCallback(async (alertId: string) => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      await FirestoreService.deleteAlert(user.id, alertId);
    } catch (error) {
      console.error('Error deleting alert:', error);
      setError('Failed to delete alert');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Delete multiple alerts
  const deleteMultipleAlerts = useCallback(async (alertIds: string[]) => {
    if (!user?.id || alertIds.length === 0) return;

    try {
      setIsLoading(true);
      await FirestoreService.deleteMultipleAlerts(user.id, alertIds);
    } catch (error) {
      console.error('Error deleting alerts:', error);
      setError('Failed to delete alerts');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Acknowledge alert (mark as seen)
  const acknowledgeAlert = useCallback(async (alertId: string) => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      await FirestoreService.acknowledgeAlert(user.id, alertId);
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      setError('Failed to acknowledge alert');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Mark alert as new (for notification lifecycle)
  const markAlertAsNew = useCallback(async (alertId: string) => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      await FirestoreService.markAlertAsNew(user.id, alertId);
    } catch (error) {
      console.error('Error marking alert as new:', error);
      setError('Failed to mark alert as new');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Filter unread alerts for the bell badge (NOT_SEEN or NEW)
  const unreadAlerts = alerts.filter(alert => 
    alert.data.visibility === 'NOT_SEEN' || alert.data.visibility === 'NEW'
  );

  const value: AlertContextType = {
    alerts,
    unreadAlerts,
    isLoading,
    error,
    deleteAlert,
    deleteMultipleAlerts,
    acknowledgeAlert,
    markAlertAsNew,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlerts = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
}; 